// Copyright (c) Bentley Systems
import "./App.css";
import "./Icons/IconStyles.css";
import "./Helpers/custom-itwin-styles.css";
import {
  Avatar,
  DropdownMenu,
  getUserColor,
  Header,
  HeaderBreadcrumbs,
  HeaderButton,
  HeaderLogo,
  IconButton,
  MenuDivider,
  MenuItem,
  ProgressRadial,
  SidenavButton,
  SideNavigation,
  Text,
  ThemeProvider,
  ThemeType,
} from "@itwin/itwinui-react";
import AreasList from "./Areas/AreasList";
import Elist from "./Elist/Elist";
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "./Layout";
import NoPage from "./ErrorPages/NoPage";
import { ErrorBoundary } from "react-error-boundary";
import ErrorPage from "./ErrorPages/ErrorPage";
import EformView from "./Eform/Eform";
import Etree from "./Etree/Etree";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  selectAccessToken,
  selectIsLoggedIn,
  setAccessToken,
  setAuthorizationClient,
  setIsLoggedIn,
} from "./app/authorization-slice";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import EiCall from "./Action/EiCall";

import {
  selectActiveAreaId,
  selectActiveNodeId,
  selectHeaders,
  selectHideMainMenu,
  selectHideSideMenu,
  selectIsSideMenuExpanded,
  selectSideMenuWidthOnly,
  setActiveAreaId,
  setActiveNodeId,
  setHideMainMenu,
  setHideSideMenu,
  setIsSideMenuExpanded,
  setMainMenuHeight,
  setSideMenuWidth,
} from "./app/header-slice";
import { PageLayout } from "@itwin/itwinui-layouts-react";
import { AreaModel } from "./Areas/Models/area-model";
import {
  getPropertyOrDefaultValue,
  GetUrlByNode,
  GetUrlByUniqueName,
} from "./Helpers/helper-functions";
import { resolveColorValue } from "./Helpers/color-utils";
import IconByName from "./Icons/IconByName";
import { setSortFilterForNode } from "./app/sort-filter-slice";
import Icons from "./Icons/Icons";
import { selectConfirmation, setConfirmation } from "./app/confirmation-slice";
import { Icon } from "./Areas/Models/icon";
import { ConfirmationDialog } from "./app/ConfirmationDialog";
import ArchiveAdd from "./Archive/ArchiveAdd";
import Archive from "./Archive/Archive";
import { selectFormOpen } from "./app/form-open-slice";
import { BrowserAuthorizationClientImpl } from "./app/browser-authorization-client-impl";
import { ConfigManager } from "./Helpers/config-manager";
import { jwtDecode } from "jwt-decode";
import CredentialsList from "./Credentials/CredentialsList/CredentialsList";
import EntryScreen from "./iTwinManagementEntryScreen/EntryScreen";
import { selectTheme, setTheme } from "./app/theme-slice";
import LinkageAttributeForm from "./iTwinManagementEntryScreen/LinkageAttributeForm";
import GlobalErrorProvider from "./GlobalErrorProvider";
import {
  selectDefaultColor,
  selectIModelValueId,
  selectIntelligentContent,
  selectIsCreatingIModelValue,
  selectIsImodelValueDefined,
  selectIsUrlValid,
  setCreateIModelValue,
  setDefaultColor,
  setIntelligentContent,
} from "./app/entry-screen-slice";
import ExternalViews from "./iTwinManagementEntryScreen/ExternalViews";
import { SvgExit } from "@itwin/itwinui-icons-react";
import ProjectManagement from "./ProjectManagement/ProjectManagement";
import SubscribedProjectList from "./SubscribedProjects/SubscribedProjectList";
import { DawComponentRoutePaths } from "./Helpers/daw-component-route-paths";
import SubscribedOrganizations from "./SubscribedOrganizations/SubscribedOrganizations";
import { EntryScreenLogic, toggleIntelligentContent } from "./iTwinManagementEntryScreen/EntryScreenLogic";
import ManageAccessRolesAssignmentForm from "./SubscribedProjects/ManageAccessRolesAssignmentForm";
import ApiTesting from "./ApiTesting/ApiTesting";
import OnPremRouting from "./OnPremRouting/OnPremRouting";
import OnPremTelemetry from "./OnPremTelemetry/OnPremTelemetry";
import IModelSelector from "./iTwinManagementEntryScreen/IModelSelector";

type TokenPayload = {
  email: string;
  given_name: string;
  family_name: string;
};

const App = () => {
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const [isMenuBarRendered, setIsMenuBarRendered] = useState(false);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const themeState = useAppSelector(selectTheme);
  const theme = useMemo((): ThemeType => {
    return themeState.isDark === true ? "dark" : "light";
  }, [themeState.isDark]);

  const areas = useAppSelector(selectHeaders);
  const activeAreaId = useAppSelector(selectActiveAreaId);
  const isSideBarOpen = useAppSelector(selectIsSideMenuExpanded);
  const sideMenuWidth = useAppSelector(selectSideMenuWidthOnly);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const menuBarRef = useRef<HTMLElement | null>(null);
  const hideSideMenu = useAppSelector(selectHideSideMenu);
  const hideMainMenu = useAppSelector(selectHideMainMenu);
  const confirmationState = useAppSelector(selectConfirmation);
  const formOpenState = useAppSelector(selectFormOpen);
  const redirectUri = ConfigManager.getBaseUrl();
  const isBackToEntryScreenQueryParameter =
    searchParams.get("backToEntryScreen");
  const authClient = new BrowserAuthorizationClientImpl({
    clientId: "edf-daw-ui-authcode",
    scope:
      "organization itwin-platform rbac-service edf:itwin-internal edf-daw-service itwinjs",
    redirectUri,
    responseType: "code",
    authority:
      window.env?.VITE_AUTHORITY ??
      (import.meta.env.VITE_AUTHORITY as string),
  });
  const [user, setUser] = useState<TokenPayload | undefined>(undefined);
  const accessToken = useAppSelector(selectAccessToken);

  const logout = async (): Promise<void> => {
    const runId = crypto.randomUUID();
    await fetch("/api/Auth/logout/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: runId,
      },
    });

    await authClient.signOut();
  };

  authClient.onAccessTokenChanged.addListener((newAccToken: string) => {
    dispatch(setAccessToken(newAccToken));
    const tokenPayload = jwtDecode<TokenPayload>(newAccToken);
    setUser(tokenPayload);
  }, []);

  const login = useCallback(async () => {
    try {
      await authClient.signInSilent();
      dispatch(setIsLoggedIn(authClient.isAuthorized));
    } catch {
      await authClient.signIn();
      dispatch(setIsLoggedIn(authClient.isAuthorized));
    }
  }, [authClient]);

  useEffect(() => {
    void ( async () => {
      dispatch(setAuthorizationClient(authClient));
      const userManager = await authClient.getOidcUserManager();
      userManager.events.addSilentRenewError((error) => {
        // eslint-disable-next-line no-console -- diagnostic info for devs; no user action possible
        console.error("Silent renew failed", error);
      });

      void login();
    })();
  }, []);

  useEffect(() => {
    const hideMainMenuParam =
      searchParams.get("hidemainmenu")?.toLowerCase() === "true";
    const hideSideMenuParam =
      searchParams.get("hidesidemenu")?.toLowerCase() === "true";
    const darkTheme = searchParams.get("darkTheme")?.toLowerCase() === "true";

    dispatch(setHideMainMenu(hideMainMenuParam));
    dispatch(setHideSideMenu(hideSideMenuParam));
    dispatch(setTheme(darkTheme));
  }, [dispatch, searchParams]);

  const abbreviation = useMemo(() => {
    return (
      (user?.given_name?.toLocaleUpperCase()?.[0] ?? "") +
      (user?.family_name?.toLocaleUpperCase()?.[0] ?? "")
    );
  }, [user]);

  function setAreasAndMenu(area: AreaModel) {
    dispatch(setActiveAreaId(area.applAreaId));
    if (area.nodeId) {
      dispatch(setActiveNodeId(area.nodeId));
    }
  }

  useLayoutEffect(() => {
    if (sidebarRef.current) {
      const width = sidebarRef.current.getBoundingClientRect().width;
      dispatch(setSideMenuWidth(width));
    } else {
      dispatch(setSideMenuWidth(0));
    }
  }, [isSidebarRendered]);

  useEffect(() => {
    const sidebarElement = sidebarRef.current;

    const handleTransitionEnd = () => {
      if (sidebarElement) {
        const width = sidebarElement.getBoundingClientRect().width;
        dispatch(setSideMenuWidth(width));
      }
    };

    if (sidebarElement) {
      sidebarElement.addEventListener("transitionend", handleTransitionEnd);
    }

    return () => {
      if (sidebarElement) {
        sidebarElement.removeEventListener(
          "transitionend",
          handleTransitionEnd,
        );
      }
    };
  }, [isSideBarOpen]);

  useLayoutEffect(() => {
    if (menuBarRef.current) {
      const height = menuBarRef.current.getBoundingClientRect().height;
      dispatch(setMainMenuHeight(height));
    } else {
      dispatch(setMainMenuHeight(0));
    }
  }, [isMenuBarRendered]);

  function getIcon(icon: Icon | undefined) {
    if (
      icon !== undefined &&
      icon !== null &&
      icon.name !== undefined &&
      icon.name !== null
    ) {
      const name = icon.name.toLowerCase();
      let iconName;
      const str = "itwinicon:";
      if (name.indexOf(str) > -1) {
        if (name.indexOf("-animated") > -1) {
          iconName = name.replace("-animated", "");
        } else {
          iconName = name;
        }
        if (iconName !== undefined && iconName !== null) {
          const iconFillColor = theme === "dark" ? "white" : "black";
          return (
            <IconByName
              style={{
                fill: iconFillColor,
              }}
              name={iconName.substring(str.length).trim()}
            />
          );
        }
      }
    }
  }

  const menuItems = (close: () => void) =>
    areas.length > 0
      ? areas.map((area) => (
          <MenuItem
            key={area.nodeId}
            startIcon={getIcon(area.icon)}
            onClick={() => {
              close();
              setAreasAndMenu(area);
              let navstr = area.nodeType === 'DC' ? GetUrlByUniqueName(area.nodeUniqueName) : GetUrlByNode(area.nodeType, area.nodeId as number);
              navstr = navstr + window.location.search;
              void navigate(navstr);
            }}
          >
            {area.name}
          </MenuItem>
        ))
      : [];

  function getActiveAreaName() {
    if (activeAreaId !== undefined) {
      const activeArea = areas.find((a) => a.applAreaId === activeAreaId);
      if (activeArea !== undefined) {
        return activeArea.name;
      }
    }
    return undefined;
  }

  function getActiveAreaIcon() {
    if (activeAreaId !== undefined) {
      const activeArea = areas.find((a) => a.applAreaId === activeAreaId);
      if (activeArea !== undefined) {
        return activeArea.icon;
      }
    }
    return undefined;
  }

  const headerBreadcrumbsItems = [
    <HeaderButton
      style={{ marginLeft: "0.75rem" }}
      key={"areasMenu"}
      startIcon={getIcon(getActiveAreaIcon())}
      name={getActiveAreaName()}
      menuItems={menuItems}
    />,
  ];

  const getBreadcrumbs =
    !hideMainMenu && areas.length > 0 && activeAreaId !== undefined ? (
      <HeaderBreadcrumbs items={headerBreadcrumbsItems} />
    ) : undefined;

  const userName = `${user?.given_name} ${user?.family_name}`;

  const avatarMenuItems = () => [
    <MenuItem key={0}>
      <Text variant="leading">{userName}</Text>
      <Text isMuted>{user?.email}</Text>
    </MenuItem>,
    <MenuDivider key={1} />,
    <MenuItem
      key={2}
      onClick={() => {
        void logout();
        //dispatch(setAccessToken(""));
        //dispatch(setIsLoggedIn(false));
        //dispatch(setAuthorizationClient());
        //navigate("/");
      }}
    >
      Sign out
    </MenuItem>,
  ];

  const getHeader = useMemo(() => {
    if (hideMainMenu === false) {
      return (
        <Header
          ref={(el) => {
            if (menuBarRef) {
              menuBarRef.current = el;
            }
            setIsMenuBarRendered(true); // Mark menu bar as rendered
          }}
          appLogo={
            <HeaderLogo
              role="button"
              tabIndex={0}
              logo={
                <Icons.BecsLogo
                  style={{
                    width: "76px",
                    height: "32px",
                    blockSize: "calc(2* var(--iui-size-m))",
                  }}
                />
              }
              onClick={() => {
                let navStr = "/";
                navStr = navStr + window.location.search;
                void navigate(navStr);
              }}
              style={{ width: "124px", marginBottom: "8px" }}
            />
          }
          breadcrumbs={getBreadcrumbs}
          actions={[
            <DropdownMenu key="avatar" menuItems={avatarMenuItems}>
              <div style={{ height: "100%" }}>
                <IconButton styleType="borderless" label={userName}>
                  <Avatar
                    size="medium"
                    backgroundColor={getUserColor(user?.email ?? "")}
                    abbreviation={abbreviation}
                  />
                </IconButton>
              </div>
            </DropdownMenu>,
          ]}
          // actions={[
          //   <Tooltip
          //     content={`Change theme to ${
          //       theme === "dark" ? "light" : "dark"
          //     }`}
          //   >
          //     <IconButton
          //       style={{ float: "right" }}
          //       size="small"
          //       styleType="borderless"
          //       onClick={() =>
          //         setTheme(theme === "dark" ? "light" : "dark")
          //       }
          //     >
          //       {theme === "dark" ? <SvgSun /> : <SvgMoon />}
          //     </IconButton>
          //   </Tooltip>,
          // ]}
        />
      );
    } else {
      return <></>;
    }
  }, [hideMainMenu, areas, activeAreaId, user]);
  const activeMenuNodeId = useAppSelector(selectActiveNodeId);

  const isEntryScreenUrlValid = useAppSelector(selectIsUrlValid);
  const isImodelValueDefined = useAppSelector(selectIsImodelValueDefined);
  const intelligentContent = useAppSelector(selectIntelligentContent);
  const defaultColor = useAppSelector(selectDefaultColor);
  const iModelValueId = useAppSelector(selectIModelValueId);
  const isCreatingIModelValue = useAppSelector(selectIsCreatingIModelValue);
  const isEnableBecsDisabled = !(
    isEntryScreenUrlValid && !isImodelValueDefined
  );
  const isEntryScreenSideMenuDisabled = !(
    isEntryScreenUrlValid && isImodelValueDefined
  );

  const getSideMenu = () => {
    const activeArea = areas.find((a) => a.applAreaId === activeAreaId);
    if (isBackToEntryScreenQueryParameter) {
      return (
        <SideNavigation
          ref={(el) => {
            if (sidebarRef) {
              sidebarRef.current = el;
            }
            setIsSidebarRendered(true); // Mark sidebar as rendered
          }}
          items={[
            <div
              key={"becsIconSb"}
              className="becs-sidenav-static-button"
              style={{ cursor: "default" }}
            >
              <span className="becs-sidenav-static-button-icon">
                <IconByName
                  name="becsIcon"
                  style={{
                    width: "24px",
                    height: "24px",
                  }}
                />
              </span>
              <span className="becs-sidenav-static-button-label">BECS</span>
            </div>,
            <div key={"iModelSelectorSb"}>
              <IModelSelector
                iTwinId={iTwinId}
                currentIModelId={iModelId}
                onIModelChange={(newIModelId) => {
                  const params = new URLSearchParams(searchParams);
                  params.set("iModelId", newIModelId);
                  void navigate(`/entry?${params.toString()}`);
                }}
              />
            </div>,
            <SidenavButton
              startIcon={
                <div className="becs-toggle-switch-wrapper" data-size="small">
                  <input
                    className="becs-toggle-switch"
                    type="checkbox"
                    role="switch"
                    tabIndex={0}
                    checked={isEnableBecsDisabled}
                    onClick={() => {}}
                    onChange={() => {}}
                    disabled={isEnableBecsDisabled}
                  />
                </div>
              }
              key={"attrSb"}
              isActive={!isEnableBecsDisabled}
              onClick={() => {
                dispatch(
                  setConfirmation({
                    isConfirmationOpen: true,
                    onOkAction: async () => {
                      dispatch(setCreateIModelValue(true));
                    },
                    caption: "Enable integration configuration",
                    text: "Do you want to proceed with enabling integration configuration?",
                    okText: "Yes",
                    cancelText: "No",
                    showCheckbox: true,
                    checkboxLabel: "Intelligent content",
                    checkboxChecked: intelligentContent,
                    onCheckboxChange: (checked: boolean) => {
                      dispatch(setIntelligentContent(checked));
                    },
                    showColorPicker: true,
                    colorPickerLabel: "Default rendering color",
                    colorPickerValue: defaultColor,
                    onColorChange: (color: string | null) => {
                      dispatch(
                        setDefaultColor(resolveColorValue(color) as string),
                      );
                    },
                  }),
                );
              }}
              disabled={isEnableBecsDisabled}
            >
              Allow configuration
              {isEnableBecsDisabled ? (
                <Text isMuted variant="small">
                  Integration configuration
                  <br />
                  enabled
                </Text>
              ) : (
                <Text isMuted variant="small">
                  Enable integration
                  <br />
                  configuration
                </Text>
              )}
            </SidenavButton>,
            ...(isEntryScreenSideMenuDisabled
              ? []
              : [
                  <SidenavButton
                    startIcon={
                      <div
                        className="becs-toggle-switch-wrapper"
                        data-size="small"
                        style={{ pointerEvents: "none" }}
                      >
                        <input
                          className="becs-toggle-switch"
                          type="checkbox"
                          role="switch"
                          tabIndex={-1}
                          checked={intelligentContent}
                          readOnly
                        />
                      </div>
                    }
                    key={"IntelContentSb"}
                    onClick={() => {
                      if (!iModelValueId || !iTwinId || !iModelId) {
                        return;
                      }
                      dispatch(
                        setConfirmation({
                          isConfirmationOpen: true,
                          onOkAction: async () => {
                            await toggleIntelligentContent(
                              iTwinId,
                              iModelId,
                              iModelValueId,
                              intelligentContent,
                              dispatch,
                              accessToken,
                            );
                          },
                          caption: "Toggle Intelligent Content",
                          text: `Do you really want to set intelligent content to ${!intelligentContent}?`,
                          okText: "Yes",
                          cancelText: "No",
                        }),
                      );
                    }}
                  >
                    Intelligent content
                  </SidenavButton>,
                ]),
            <SidenavButton
              startIcon={getIconByName("linkageAttribute")}
              key={"LinkageSb"}
              isActive={window.location.pathname.includes(
                DawComponentRoutePaths.DAW_COMPONENT_LINKAGE_ATTRIBUTE_FORM,
              )}
              onClick={() => {
                navigateToUrl("LinkageAttribute");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Linkage attribute
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("TabListing")}
              key={"tabsSb"}
              isActive={window.location.pathname.includes(tabsUrl)}
              onClick={() => {
                navigateToUrl("Tabs");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Define tabs
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("passwordManagement")}
              key={"credSb"}
              isActive={window.location.pathname.includes(
                DawComponentRoutePaths.DAW_COMPONENT_CREDENTIAL_LIST,
              )}
              onClick={() => {
                navigateToUrl("Credentials");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              System credentials
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("project")}
              key={"projectSb"}
              isActive={window.location.pathname.includes(projectsUrl)}
              onClick={() => {
                navigateToUrl("Projects");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              User integration project
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("attributes")}
              key={"attsSb"}
              isActive={window.location.pathname.includes(attributesUrl)}
              onClick={() => {
                navigateToUrl("Attributes");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Select attributes
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("mapping")}
              key={"mappingSb"}
              isActive={window.location.pathname.includes(mappingUrl)}
              onClick={() => {
                navigateToUrl("Mapping");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Mapping
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("interoperability")}
              key={"operatorsSb"}
              isActive={window.location.pathname.includes(operatorsUrl)}
              onClick={() => {
                navigateToUrl("Operators");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Operators
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("colorsettings")}
              key={"colorsettingsSb"}
              isActive={window.location.pathname.includes(colorsettingsUrl)}
              onClick={() => {
                navigateToUrl("Colorsettings");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Coloring
            </SidenavButton>,
            <SidenavButton
              startIcon={getIconByName("log")}
              key={"logSb"}
              isActive={window.location.pathname.includes(logUrl)}
              onClick={() => {
                navigateToUrl("Log");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              Dynamic integration log
            </SidenavButton>,
            <SidenavButton
              startIcon={<SvgExit />}
              key={"ExternalViewsSb"}
              isActive={window.location.pathname.includes(
                DawComponentRoutePaths.DAW_COMPONENT_EXTERNAL_VIEWS,
              )}
              onClick={() => {
                navigateToUrl("ExternalViews");
              }}
              disabled={isEntryScreenSideMenuDisabled}
            >
              External views
            </SidenavButton>,
          ]}
          isExpanded={isSideBarOpen}
          onExpanderClick={() => {
            dispatch(setIsSideMenuExpanded(!isSideBarOpen));
          }}
        />
      );
    } else {
      if (
        !hideSideMenu &&
        activeArea !== undefined &&
        activeArea.menu &&
        activeArea.menu.menuItems &&
        activeArea.menu.menuItems.length > 0
      ) {
        const mainItems = activeArea.menu?.menuItems.map((menuItem) => (
          <SidenavButton
            startIcon={getIconByName(menuItem.dawSvgIcon)}
            key={menuItem.nodeId}
            isActive={activeMenuNodeId === menuItem.nodeId}
            onClick={() => {
              dispatch(setActiveNodeId(menuItem.nodeId as number));
              dispatch(
                setSortFilterForNode({
                  nodeId: menuItem.nodeId?.toString(),
                  sortFilterState: {
                    sortBy: [],
                    filters: [],
                  },
                }),
              );

              let link = menuItem.nodeType === 'DC' ? GetUrlByUniqueName(menuItem.nodeUniqueName) : GetUrlByNode(
                menuItem.nodeType,
                menuItem.nodeId as number,
              );
              link = link + window.location.search;
              void navigate(link);
            }}
          >
            {menuItem.name}
          </SidenavButton>
        ));

        return (
          <SideNavigation
            items={mainItems}
            isExpanded={isSideBarOpen}
            ref={(el) => {
              if (sidebarRef) {
                sidebarRef.current = el;
              }
              setIsSidebarRendered(true); // Mark sidebar as rendered
            }}
            onExpanderClick={() => {
              dispatch(setIsSideMenuExpanded(!isSideBarOpen));
            }}
            // secondaryItems={[
            //   <SidenavButton startIcon={<SvgSettings />} key={3}>
            //     Settings
            //   </SidenavButton>,
            // ]}
          />
        );
      } else {
        return undefined;
      }
    }

    function getIconByName(
      svgIconName: string | undefined,
    ): React.JSX.Element | undefined {
      if (svgIconName === undefined || svgIconName === null) {
        return undefined;
      }
      return <IconByName name={svgIconName} />;
    }
  };

  // Read the URL parameters
  const iTwinId = searchParams.get("iTwinId");
  const iModelId = searchParams.get("iModelId");

  const iModelDescription = useAppSelector(
    (state) => state.entryScreen.iModelDescription,
  );

  // EntryScreen logic component for URL validation and data fetching
  const entryScreenLogic = (
    <EntryScreenLogic
      isActive={isBackToEntryScreenQueryParameter === "true"}
      iTwinId={iTwinId}
      iModelId={iModelId}
    />
  );

  const projectsUrl = `/elist/246`;
  const tabsUrl = `/elist/310`;
  const attributesUrl = `/elist/194`;
  const colorsettingsUrl = `/elist/221`;
  const mappingUrl = `/elist/180`;
  const operatorsUrl = `/elist/295`;
  const logUrl = `/elist/283`;
  //Construct the URL to navigate to
  const navigateToUrl = (whereTo: string) => {
    if (iTwinId && iTwinId) {
      let url = "";
      switch (whereTo) {
        case "LinkageAttribute":
          url = DawComponentRoutePaths.DAW_COMPONENT_LINKAGE_ATTRIBUTE_FORM;
          break;
        case "Projects":
          url = `${projectsUrl}/iModelID=${iModelId}&ProjectId=${iTwinId}&iTwinId=${iTwinId}&IMODEL_DESC=${iModelDescription}`;
          break;
        case "Credentials":
          url = `${DawComponentRoutePaths.DAW_COMPONENT_CREDENTIAL_LIST}/iModelID=${iModelId}&iTwinId=${iTwinId}&IMODEL_DESC=${iModelDescription}`;
          break;
        case "Tabs":
          url = tabsUrl;
          break;
        case "Attributes":
          url = `${attributesUrl}/iModelID=${iModelId}&ProjectId=${iTwinId}&IMODEL_DESC=${iModelDescription}`;
          break;
        case "Colorsettings":
          url = `${colorsettingsUrl}/iModelID=${iModelId}&IMODEL_DESC=${iModelDescription}&ProjectId=${iTwinId}`;
          break;
        case "Mapping":
          url = `${mappingUrl}/ELF_iModelID=${iModelId}&IMODEL_DESC=${iModelDescription}&iModelID=${iModelId}&ProjectId=${iTwinId}`;
          break;
        case "Operators":
          url = `${operatorsUrl}/iModelID=${iModelId}&ProjectId=${iTwinId}&IMODEL_DESC=${iModelDescription}`;
          break;
        case "Log":
          url = logUrl;
          break;
        case "ExternalViews":
          url = DawComponentRoutePaths.DAW_COMPONENT_EXTERNAL_VIEWS;
          break;
      }
      url = url + window.location.search;
      void navigate(url);
    } else {
      alert("Missing URL parameters!");
    }
  };

  return (
    <ThemeProvider theme={theme} themeOptions={{ applyBackground: true }}>
      <ErrorBoundary
        FallbackComponent={ErrorPage}
        onError={() => {
          //errorService.log({ error, errorInfo })
        }}
        onReset={() => {
          // reset the state of your app
        }}
      >
        {isLoggedIn ? (
          <div>
            {entryScreenLogic}
            <GlobalErrorProvider />
            {isCreatingIModelValue && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <ProgressRadial size="large" />
                <Text
                  style={{
                    color: "white",
                    marginTop: "16px",
                    fontSize: "16px",
                  }}
                >
                  Enabling BECS...
                </Text>
              </div>
            )}
            <ConfirmationDialog
              isOpen={confirmationState.isConfirmationOpen}
              caption={getPropertyOrDefaultValue(
                confirmationState.caption,
                "Confirm Action",
              )}
              text={getPropertyOrDefaultValue(
                confirmationState.text,
                "Are you sure?",
              )}
              okText={getPropertyOrDefaultValue(confirmationState.okText, "")}
              cancelText={getPropertyOrDefaultValue(
                confirmationState.cancelText,
                "",
              )}
              showCheckbox={confirmationState.showCheckbox}
              checkboxLabel={confirmationState.checkboxLabel}
              checkboxChecked={confirmationState.checkboxChecked}
              onCheckboxChange={confirmationState.onCheckboxChange}
              showColorPicker={confirmationState.showColorPicker}
              colorPickerLabel={confirmationState.colorPickerLabel}
              colorPickerValue={confirmationState.colorPickerValue}
              onColorChange={confirmationState.onColorChange}
              onConfirmationAction={() => {
                if (confirmationState.onOkAction !== undefined) {
                  confirmationState.onOkAction();
                }
                dispatch(setConfirmation({ isConfirmationOpen: false }));
              }}
              onCancelAction={() => {
                dispatch(setConfirmation({ isConfirmationOpen: false }));
              }}
            />
            <div
              style={
                confirmationState.isConfirmationOpen
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              {confirmationState.isConfirmationOpen && (
                <div className="overlay" />
              )}
              <PageLayout>
                <PageLayout.Header>
                  <div
                    style={
                      formOpenState.isOpen
                        ? { pointerEvents: "none", opacity: "0.8" }
                        : {}
                    }
                  >
                    {formOpenState.isOpen && hideMainMenu === false && (
                      <div className="overlay" style={{ height: "60px" }} />
                    )}
                    {getHeader}
                  </div>
                </PageLayout.Header>

                <PageLayout.SideNavigation>
                  <div
                    className="side-menu-scrollable"
                    style={
                      formOpenState.isOpen
                        ? {
                            pointerEvents: "none",
                            opacity: "0.8",
                            height: "100%",
                          }
                        : { height: "100%" }
                    }
                  >
                    {formOpenState.isOpen && hideSideMenu === false && (
                      <div
                        className="overlay"
                        style={{ width: sideMenuWidth ? sideMenuWidth : 0 }}
                      />
                    )}
                    {getSideMenu()}
                  </div>
                </PageLayout.SideNavigation>

                <PageLayout.Content>
                  <Routes>
                    <Route path="/" element={<Layout />}>
                      <Route index element={<AreasList />} />
                      <Route path="entry" element={<EntryScreen />} />
                      <Route
                        path={
                          DawComponentRoutePaths.DAW_COMPONENT_LINKAGE_ATTRIBUTE_FORM
                        }
                        element={<LinkageAttributeForm />}
                      />
                      <Route
                        path={
                          DawComponentRoutePaths.DAW_COMPONENT_EXTERNAL_VIEWS
                        }
                        element={<ExternalViews />}
                      />
                      <Route
                        path={`${DawComponentRoutePaths.DAW_COMPONENT_EXTERNAL_VIEWS}/:params`}
                        element={<ExternalViews />}
                      />
                      <Route path="areas" element={<AreasList />} />
                      <Route path="elist/:id" element={<Elist />} />
                      <Route path="elist/:id/:params" element={<Elist />} />
                      <Route path="etree/:id" element={<Etree />} />
                      <Route path="etree/:id/:params" element={<Etree />} />
                      <Route path="archive/:id" element={<Archive />} />
                      <Route path="archive/:id/:params" element={<Archive />} />
                      <Route path="archiveadd/:id" element={<ArchiveAdd />} />
                      <Route
                        path="archiveadd/:id/:params"
                        element={<ArchiveAdd />}
                      />
                      <Route
                        path="eform/:id"
                        element={<EformView isFromUrl={true} />}
                      />
                      <Route
                        path="eform/:id/:params"
                        element={<EformView isFromUrl={true} />}
                      />
                      {/* Credentials List routes - with and without params */}
                      {(() => {
                        const credentialListPath = DawComponentRoutePaths.DAW_COMPONENT_CREDENTIAL_LIST;
                        return (
                          <>
                            <Route path={`${credentialListPath}/:params`} element={<CredentialsList />} />
                            <Route path={credentialListPath} element={<CredentialsList />} />
                          </>
                        );
                      })()}
                      <Route
                        path={
                          DawComponentRoutePaths.DAW_COMPONENT_GENERAL_PROJECTS
                        }
                        element={<ProjectManagement />}
                      />
                      <Route
                        path={
                          DawComponentRoutePaths.DAW_COMPONENT_SUBSCRIBED_PROJECTS
                        }
                        element={<SubscribedProjectList />}
                      />
                      <Route
                        path={
                          DawComponentRoutePaths.DAW_COMPONENT_SUBSCRIBED_ORGANIZATIONS
                        }
                        element={<SubscribedOrganizations />}
                      />
                      <Route
                        path={
                          `${DawComponentRoutePaths.DAW_COMPONENT_MANAGE_ACCESS_ROLES_ASSIGNMENT_FORM 
                          }/:params`
                        }
                        element={<ManageAccessRolesAssignmentForm />}
                      />
                      <Route path="eicall/:id" element={<EiCall />} />
                      <Route path="eicall/:id/:params" element={<EiCall />} />
                      <Route
                        path={DawComponentRoutePaths.DAW_COMPONENT_API_TESTING}
                        element={<ApiTesting />}
                      />
                      <Route
                        path={DawComponentRoutePaths.DAW_COMPONENT_ON_PREM_ROUTING}
                        element={<OnPremRouting />}
                      />
                      <Route
                        path={DawComponentRoutePaths.DAW_COMPONENT_ON_PREM_TELEMETRY}
                        element={<OnPremTelemetry />}
                      />
                      {/* error pages */}
                      <Route path="errorPage" element={<ErrorPage />} />
                      <Route path="*" element={<NoPage />} />
                    </Route>
                  </Routes>
                </PageLayout.Content>
              </PageLayout>
            </div>
          </div>
        ) : (
          <></>
        )}
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
